import api from '@/api'
import Vue from 'vue';
import permissions from '@/api/permissions'
import _ from 'lodash'
import { evaluate } from 'mathjs'
import AdvancePaymentBlockReason from "../api/types/advance-payment-block-reason";
import OperationType from "../api/types/operation-type";

const findPaymentTypeAndNum = function(paymentId, config) {
  let num = paymentId;
  let payType = 'Type1';
  let type = 'contract';

  if (num > config.datePointContract) {
    num -= config.datePointContract;
    payType = 'Type2';
    type = 'delivery';

    if (num > config.datePointDelivery) {
      num -= config.datePointDelivery;
      payType = 'Type3';
      type = 'accept';
    }
  }

  return {
    num,
    payType,
    type
  }
};

const fillRequests = function(data) {
  const requests = (data.requests || []).map(e => {
    const requestSummary = _.find(data.requestsTotal || [], { request: e.num });

    // in order to calculate present price for position in spec we have to multiple estimation of all expert criteria to theirs weight
    return {
      ...e,
      bids: e.bids.map(b => {
        let specs = {};

        const result = {
          ...b,
          state: new api.BidState(b.state),
          confirmingState: b.approvalState ? new api.BidState(b.approvalState) : undefined,
          stateShouldBeConfirmed: b.approvalState && b.approvalState !== b.state,
          currencyPay: b.currencyPay ? new api.Currency(b.currencyPay) : undefined,
          currencyBid: b.currencyBid ? new api.Currency(b.currencyBid) : undefined,
        };

        if (requestSummary) {
          specs = b.specs.map(s => {
            const estimate = _.filter(requestSummary.criterions, { bidNum: b.num, position: s.position }).reduce((acc, c) => {
              const crit = _.find(data.criterions, { num: c.num });
              return acc + c.estimate * _.get(crit, 'weight');
            }, 0);

            return {
              ...s,
              estimate
            }
          });

          result.specs = specs;
          result.estimate = _.max(specs.map(e => e.estimate));
        }

        return result;
      }),
      state: new api.RequestState(e.state),
      confirmingState: e.approvalState ? new api.RequestState(e.approvalState) : undefined,
      stateShouldBeConfirmed: e.approvalState && e.approvalState !== e.state,
    }
  });

  const flattenBids = _.orderBy(requests.reduce((acc, req) => {
    if (req.state.DECLINED || req.state.DELETED) {
      return acc;
    }

    return acc.concat(req.bids.map(b => ({
      rating: b.rating,
      time: b.time,
      bid: b
    })))
  }, []), ['rating', 'time'], ['desc', 'asc']);

  for (let i = 0; i < flattenBids.length; ++i) {
    flattenBids[i].bid.rank = flattenBids[i].rating;
  }

  return requests;
};

const fillRetends = function(data) {
  return _.sortBy((data.retends || []).map(e => ({
    hasSign: e.hasSign,
    serial: e.no,
    step: e.step,
    currency: new api.Currency(e.currency),
    waitingTime: e.waitingTime,
    startTime: new Date(e.timeBegin),
    endTime: new Date(e.timeEnd),
    calculatedEndTime: new Date(e.timeEndCalculate),
    state: new api.RetendState(e.state),
  })), 'serial');
};

const fillLeader = function(model, data) {
  const best = _.get(data, 'requestsTotal.best');
  if (!_.isEmpty(best)) {
    const leader = model.specs.reduce((acc, s) => {
      acc[s.position] = {
        criteria: {}
      };

      return acc;
    }, {});

    return best.reduce((acc, e) => {
      const count = _.get(_.find(model.specs, { position: e. position }), 'count', 0);
      const positionCriteria = _.filter(e.criterions, c => c.position === e.position);
      const leaderPrice = _.filter(e.criterions, c => c.position === e.position && !!_.find(model.criteria.lotPrice, { num: c.num }))
        .reduce((sum, c) => sum + _.toNumber(c.value), 0);

      acc[e.position] = {
        bid: e.bid,
        presentPrice: e.presentPrice,
        total: leaderPrice * count,
        criteria: e.criterions.reduce((list, c) => {
          list[c.num] = {
            value: c.value
          };
          return list;
        }, {})
      };

      return acc;
    }, leader)
  }
};

const fillClarifications = function(source) {
  return source.map(e => ({
    num: e.num,
    bidNum: e.bidNum,
    request: {
      text: e.askText,
      hasSign: e.hasAskSign,
      time: api.Date.fromAPI(e.askTime),

      files: e.files.filter(f => f.askUid).map(f => ({
        num: f.num,
        uid: f.askUid,
        name: f.name,
        size: f.size,
        hasSign: f.hasAskUidSign
      }))
    },
    response: {
      text: e.answerText,
      hasSign: e.hasAnswerSign,
      time: api.Date.fromAPI(e.answerTime),

      files: e.files.filter(f => f.answerUid).map(f => ({
        num: f.num,
        uid: f.answerUid,
        name: f.name,
        size: f.size,
        hasSign: f.hasAnswerUidSign
      }))
    },
    permissions: permissions.init(e.permissions)
  }));
};

const applyRules = function(model, part, payment) {
  for (const rule of model.schedule.paymentTypes) {
    // if (payment.type === 'contract' && payment.part >= rule.fromPart && payment.part <= rule.toPart) {
    //   payment.isBlocked = true;
    //   payment.blockedReason = new api.AdvancePaymentBlockReason(api.AdvancePaymentBlockReason.RULES);
    // }
    // else if (payment.type !== 'contact')
    {
      // if (payment.part >= rule.fromPart && payment.part <= rule.toPart) {
      //   payment.copyTo = Array.from({ length: rule.toPart - rule.fromPart + 1 }, (v, i) => rule.fromPart + i);
      // }
      //
      if (payment.part === rule.fromPart && rule.toPart !== rule.fromPart) {
        payment.copyTo = Array.from({ length: rule.toPart - rule.fromPart }, (v, i) => rule.fromPart + i + 1);
      }
      // else if (payment.part >= rule.fromPart + 1 && payment.part <= rule.toPart) {
      //   payment.isDisabled = true;
      // }
    }
  }
};

const createLot = function(data) {
  const priceCriteria = data.criterions.filter(e => e.type === 'PriceLot' || e.type === 'Price');
  const reductionCriteria = data.criterions.filter(e => e.type === 'Reduction');

  const model = {
    id: data.id,
    catalog: {
      name: data.lotNum,
      id: data.lotId,
    },
    serverTime: +data.now,
    serverDeltaTime: +data.now - Date.now(),
    isCommentAllowed: !data.commentTimeOut,
    hasSign: data.hasSign,
    tenderNum: data.num,
    tenderId: data.tenderId,
    identifier: data.lotNum,
    state: new api.LotState(data.state),
    name: data.name,
    description: data.description,
    additionalInfo: data.additionalInfo,
    changeLotDescription: data.changeDescription,
    changeTenderDescription: data.changeTenderDescription,
    currency: new api.Currency(data.currency),
    withTaxes: data.nds,
    commission: data.commissions,
    lastChangeDate: data.changed ? new Date(data.changed) : undefined,
    publicationDate: new Date(data.time) || undefined,
    receivingRequestsDueDate: new Date(data.gettingReqEnd),
    initialPrice: data.initialPrice,
    method: new api.TenderMethod(data.method),
    nbOfLeaders: api.Number.fromAPI(data.countLeader),

    antisniper: api.Number.fromAPI(data.antisniper),
    showAntisniper: !!data.showAntisniper,

    threshold: {
      isSet: data.edgeMoney !== undefined,
      isPercent: !data.edgeMoney,
      value: +data.edgeValue
    },
    schedule: {
      paymentTypes: (data.paymentTypes || []).map(r => ({
        startDate: api.Date.fromAPI(r[2]),
        fromPart: api.Number.fromAPI(r[0]),
        toPart: api.Number.fromAPI(r[1])
      }))
    },
    formula: data.formula,

    numberOfParts: data.countPart || 0,
    numberOfPayments: data.countPay || 0,

    originator: {
      lastName: data.lastName,
      firstName: data.firstName,
      middleName: data.middleName,
      email: data.email,
      phone: data.phone
    },

    isSignedByOriginator: data.signOrganizer,
    isSignatureRequired: data.signParticipant,

    participationMode: new api.TenderParticipationMode(data.open ? api.TenderParticipationMode.OPEN: api.TenderParticipationMode.CLOSED),

    durations: {
      receivingRequests: {
        actual: data.gettingRequestDaysFact,
      },
      reviewingRequests: {
        planned: data.processingRequestDays,
        actual: data.processingRequestDaysFact
      },
      reviewingRequestsByCriteria: {
        planned: data.processingRequestByCriterionDays,
        actual: data.processingRequestByCriterionDaysFact
      },
      waitingForFirstRetend: {
        planned: data.waitingFirstRetendDays,
        actual: data.waitingFirstRetendDaysFact
      },
      retend: {
        planned: data.retendDays,
        actual: data.retendDaysFact
      },
      accept: {
        planned: data.acceptDays,
        actual: data.acceptDaysFact
      },
      splittingPrice: {
        planned: data.dividingPriceDays,
        actual: data.dividingPriceDaysFact
      },
      tender: {
        planned: data.tenderDays,
        actual: data.tenderDaysFact
      },
      contractConclusion: {
        planned: data.contractDays,
        actual: data.contractDaysFact
      },
      remind: data.remindDays
    },

    statistics: {
      sawLot: (data.statistics || []).filter(e => e.view),
      sawDocuments: (data.statistics || []).filter(e => !e.view),
    },

    categories: (data.categories || []),

    requestsTotal: data.requestsTotal,

    originalCriteria: data.criterions,

    criteria: {
      lotPrice: _.orderBy(data.criterions.filter(e => e.type === 'PriceLot').map(e => ({
        ...e,
        unit: new api.AssessmentUnit(e.positionType),
        format: new api.ProposalFormat(e.view),
        id: e.criterion,
        alias: `K${_.findIndex(priceCriteria, { criterion: e.criterion }) + 1}`
      })), 'id'),
      price: _.orderBy(data.criterions.filter(e => e.type === 'Price').map(e => ({
        ...e,
        unit: new api.AssessmentUnit(e.positionType),
        format: new api.ProposalFormat(e.view),
        id: e.criterion,
        alias: `K${_.findIndex(priceCriteria, { criterion: e.criterion }) + 1}`
      })), 'id'),
      reduction: _.orderBy(data.criterions.filter(e => e.type === 'Reduction').map(e => ({
        ...e,
        unit: new api.AssessmentUnit(e.positionType),
        format: new api.ProposalFormat(e.view),
        id: e.criterion,
        alias: `K${_.findIndex(reductionCriteria, { criterion: e.criterion }) + 1}`
      })), 'id'),
      expert: _.orderBy(data.criterions.filter(e => e.type === 'Expert').map(e => ({
        ...e,
        id: e.criterion,
        unit: new api.AssessmentUnit(e.positionType)
      })), 'id'),
      reducedPrice: _.orderBy(data.criterions.filter(e => e.type === 'ReducedPrice').map(e => ({
        ...e,
        id: e.criterion
      })), 'id')
    },

    datePoints: {
      datePointContract: api.Number.fromAPI(data.datePointContract),
      datePointDelivery: api.Number.fromAPI(data.datePointDelivery),
      datePointReceive: api.Number.fromAPI(data.datePointReceive)
    },

    comments: _.orderBy((data.comments || []).map(e => ({
      id: e.id,
      author: e.userFio,
      text: e.askText,
      file: {
        uid: e.fileUid,
        name: e.fileName
      },
      time: new Date(e.askTime),
      permissions: permissions.init(e.permissions)
    })), 'time'),

    permissions: permissions.init(data.permissions),

    retends: fillRetends(data),

    files: data.files || [],
    tenderFiles: data.tenderFiles || [],

    documents:  (data.requestDocuments || []),

    specs: (data.specs || []).map(e => ({
      catalog: {
        id: e.nomenalture,
        name: e.name
      },
      okei: e.okei,
      okeiName: e.okeiName,
      count: e.count,
      price: e.price,
      percent: !!e.percent,
      currency: new api.Currency(e.currency),
      bidCurrency: _.map(e.bidCurrency, c => new api.Currency(c)),
      currencyContract: _.map(e.payCurrency, c => new api.Currency(c)),
      currencyClause: new api.CurrencyClauseOptional(e.currencyClause),
      parts: e.parts,
      position: e.position,
      parentPosition: e.parentPosition,
      request: e.request,
      bidNum: e.bidNum,
      winner: e.winner
    })),

    extraSpecs: (data.extraSpecs || []).map(e => ({
      extraCost: e.extraCost,
      extraCostDescription: e.description,
      count: e.count,
      price: e.price,
      percent: !!e.percent,
      currency: new api.Currency(e.currency),
      bidCurrency: _.map(e.bidCurrency, c => new api.Currency(c)),
      currencyContract: _.map(e.payCurrency, c => new api.Currency(c)),
      currencyClause: new api.CurrencyClauseOptional(e.currencyClause),
      position: e.position,
      parentPosition: e.parentPosition,
      request: e.request,
      bidNum: e.bidNum,
      parts: e.parts
    })),

    requests: fillRequests(data),
    bidsHistory: (data.history || []).map(e => ({
      ...e,
      state: new api.BidState(e.state)
    })),
    lotHistory: (data.historyLot || []).map(e => ({
      ...e,
      time: api.Date.fromAPI(e.time)
    })),
    showWeight: !!data.showWeight,
    checkDates: data.checkDates,
    dates: data.dates,
    datesReceive: data.datesReceive
    // dates: data.dates.map((e, i) => data.checkDates[i] && e ? +e : 0),
    // datesReceive: data.datesReceive.map((e, i) => data.checkDates[i] && e ? +e : 0)
  };

  model.specs.sort((a,b) => a.position - b.position);

  //_.orderBy(data.protocols, 'version')
  const sortedProtocols = _.orderBy(data.protocols, ['version', 'startTime']);
  const sortedDraftProtocols = _.orderBy(data.draftProtocols, ['version', 'beginTime']);

  const protocols = _.orderBy(sortedProtocols.reduce((acc, item) => {
    const drafts = _.filter(sortedDraftProtocols, {type: item.type});

    acc[item.type] = acc[item.type] || {
      type: new api.ProtocolType(item.type),
      startTime: new Date(item.beginTime),
      permissions: permissions.init(item.permissions),

      drafts: drafts.map(d => {
        return {
          ...d,
          visible: false,
          results: d.results.map(v => ({
            ...v,
            result: api.Enum.fromAPI(v.result, api.Votes, api.Votes.EMPTY)
          })),
          votes: d.votes.map(v => ({
            ...v,
            vote: api.Enum.fromAPI(v.vote, api.Votes, api.Votes.EMPTY)
          })),
          permissions: permissions.init(d.permissions)
        }
      })
    };

    if (item.version === -1 && acc[item.type].type.ACCEPT) {
      acc[item.type].returnReason = item.reason;
      acc[item.type].returnReasons = (acc[item.type].returnReasons || []);
      acc[item.type].returnReasons.push({
        reason: item.reason,
        time: acc[item.type].startTime
      });
      return acc;
    }

    acc[item.type].files = acc[item.type].files || [];
    acc[item.type].files.push({
      permissions: permissions.init(item.permissions),
      uid: item.uid,
      name: item.fileName,
      hasFile: !!item.uid,
      hasSign: item.hasSign,
      version: item.version,
      isActual: !!item.active
    });

    return acc;
  }, {}), 'startTime');

  for (let type in protocols) {
    const item = protocols[type];
    const empty = _.findLast(item.files, {hasFile: false});

    if (!empty) {
      item.files.push({
        hasFile: false
      })
    }
  }

  model.protocols = _.orderBy(_.values(protocols), 'startTime');

  const reductionResult = _.orderBy(data.result||[], 'version').reduce((acc, item) => {
    acc[item.type] = acc[item.type] || {
      type: new api.ProtocolType(item.type),
      startTime: new Date(item.beginTime),
      permissions: permissions.init(item.permissions),
    };

    acc[item.type].files = acc[item.type].files || [];

    acc[item.type].files.push({
      permissions: permissions.init(item.permissions),
      uid: item.uid,
      hasFile: !!item.uid,
      hasSign: item.hasSign,
      version: item.version,
      isActual: !!item.active
    });

    return acc;
  }, {});

  for (let type in reductionResult) {
    const item = reductionResult[type];
    const empty = _.findLast(item.files, {hasFile: false});

    item.hasFile = _.some(item.files, {hasFile: true});

    if (!empty) {
      item.files.push({
        hasFile: false
      })
    }
  }

  model.reductionResult = _.orderBy(_.values(reductionResult), 'startTime');

  if (data.request) {
    const request = data.request;

    model.request = {
      id: request.id,
      hasSign: request.hasSign,
      permissions: permissions.init(request.permissions),

      bidRules: request.bidRules,
      additionalBidRules: request.additionalBidRules,

      state: new api.RequestState(request.approvalState || request.state || 'None'),
      stateOriginal: new api.RequestState(request.approvalState || request.state || 'None'),
      confirmedState: request.approvalState && request.approvalState !== request.state ? new api.RequestState(request.state || 'None') : undefined,

      reason: request.approvalDeclineReason || request.declineReason,
      confirmedReason: request.approvalDeclineReason && request.approvalDeclineReason !== request.declineReason ? request.declineReason : undefined,

      regulationsReason: request.approvalReglamentReason || request.reglamentReason,
      confirmedRegulationsReason: request.approvalReglamentReason && request.approvalReglamentReason !== request.reglamentReason ? request.reglamentReason : undefined,

      clarificationRequirement: request.approvalСlarificationRequirement || request.clarificationRequirement,
      confirmedСlarificationRequirement: request.clarificationRequirement,

      isFilled: !!request.filled,
      minimumDeliveryDate: request.minimumDeliveryDate ? new Date(request.minimumDeliveryDate) : undefined,

      history: _.orderBy((request.history || []).map(h => {
        let section = api.ChangedSection.SPECIFICATION;
        let criterion;

        if (h.document) {
          section = api.ChangedSection.DOCUMENT;
        }
        else if (h.criterion) {
          section = api.ChangedSection.CRITERION;

          criterion = _.find(model.criteria.price, { num: h.criterion });

          if (!criterion) {
            criterion = _.find(model.criteria.expert, { num: h.criterion });
          }

          if (!criterion) {
            criterion = _.find(model.criteria.reduction, { num: h.criterion });
          }
        }

        return {
          bidNum: h.bidNum,
          role: new api.UserRole(h.role),
          author: h.owner,
          time: api.Date.fromAPI(h.time),
          till: api.Date.fromAPI(h.tillChange),
          description: h.description,
          changedSection: new api.ChangedSection(section),
          criterion
        }
      }), 'time')
    };

    if (request.changeData) {
      model.request.userChangesHistory = request.changeData.map(e => ({
        serial: e.no,
        position: e.position,
        criterion: e.criterion,
        document: e.document,
        name: e.name,
        value: {
          old: e.oldValue,
          new: e.newValue
        },
        user: {
          firstName: e.firstName,
          lastName: e.lastName,
          middleName: e.middleName
        },
        part: e.part,
        payNum: e.payNum,
        payType: e.payType,
        bidNum: e.bidNum,
        field: api.Enum.fromAPI(e.field, api.OperationField),
        object: api.Enum.fromAPI(e.object, api.OperationSubject),
        type: api.Enum.fromAPI(e.type, api.OperationType),
        time: api.Date.fromAPI(e.time)
      }))
    }

    model.request.ownBids = _.get(data, 'requestsTotal.ownBids', []);
    model.request.leader = fillLeader(model, data);

    if (data.request.owner) {
      model.request.owner = {
        id: request.owner.id,
        lastName: request.owner.lastName,
        firstName: request.owner.firstName,
        middleName: request.owner.middleName,
        status: new api.PersonStatus(request.owner.resident ? 'resident' : 'non-resident'),
        phone: request.owner.phone,
        email: request.owner.email,

        organization: {
          type: new api.OrganizationType(request.owner.organizationType),
          name: request.owner.fullName
        }
      }
    }

    model.request.ownerHistory = (data.request.ownerHistory || []).map(item => ({
      time: api.Date.fromAPI(item.time),
      id: item.owner.id,
      lastName: item.owner.lastName,
      firstName: item.owner.firstName,
      middleName: item.owner.middleName,
      status: new api.PersonStatus(item.owner.resident ? 'resident' : 'non-resident'),
      phone: item.owner.phone,
      email: item.owner.email,

      organization: {
        type: api.Enum.fromAPI(item.owner.organizationType, api.OrganizationType),
        name: item.owner.fullName
      }
    }));

    model.request.bids = _.orderBy((request.bids || []).map(bid => {
      const bidCriterions = _.orderBy(bid.criterions, 'lastChange', 'desc');

      const result = {
        ...bid,
        isTooHigh: !!bid.tooHigh,
        sentTime: bid.time ? new Date(bid.time) : undefined,
        acceptTime: api.Date.fromAPI(bid.acceptTime),
        acceptTimeOriginal: api.Date.fromAPI(bid.acceptTime),
        priceInRequest: (bid.startSumm || []).map(e => ({
          currency: api.Enum.fromAPI(e.currency, api.Currency),
          total: api.Number.fromAPI(e.summ)
        })),

        state: new api.BidState(bid.approvalState || bid.state),
        stateOriginal: new api.BidState(bid.approvalState || bid.state),

        confirmedState: bid.approvalState && bid.approvalState !== bid.state ? new api.BidState(bid.state || 'None') : undefined,

        regulationsReason: bid.approvalReglamentReason || bid.reglamentReason,
        confirmedRegulationsReason: bid.approvalReglamentReason && bid.approvalReglamentReason !== bid.reglamentReason ? bid.reglamentReason : undefined,

        reason: bid.approvalReason || bid.reason,
        confirmedReason: bid.approvalReason && bid.approvalReason !== bid.reason ? request.reason : undefined,

        dividingFiles: bid.dividingFiles || [],
        filesState: bid.filesState || [],

        permissions: permissions.init(bid.permissions || ''),
        currencyBid: new api.Currency(bid.currencyBid),
        currencyPay: new api.Currency(bid.currencyPay),

        till: api.Date.fromAPI(bid.till),
        tillDescription: bid.tillDescription
      };

      result.documents = _.filter(model.documents, {type: 'ForBid'}).map(e => {
        const doc = _.find(bid.documents, {document: e.id}) || {};

        return {
          ...e,
          ...doc,
          expirationDate: doc.time ? new Date(doc.time) : undefined,
          state: new api.RequestDocumentState(doc.state),
          reason: doc.reason || undefined,
          files: doc.files || [],
          permissions: permissions.init(doc.permissions || ''),

          till: api.Date.fromAPI(doc.till),
          tillDescription: doc.tillDescription
        }
      });

      result.clarifications = fillClarifications(bid.clarifications || []);

      // create a list of all positions we have
      const fullSpecs = _.uniqBy(
        [].concat(
          model.specs.filter(s => !s.request || s.bidNum === bid.num).map(s => _.pick(s, 'position', 'bidNum')),
          model.extraSpecs.filter(s => !s.request || s.bidNum === bid.num).map(s => _.pick(s, 'position', 'bidNum')),
          bid.specifications.map(s => _.pick(s, 'position', 'bidNum')),
        ), 'position'
      );

      // fill the space
      fullSpecs.forEach(spec => {
        const lotSpecItem = _.find(model.specs, s => s.position === spec.position && (s.bidNum === 0 || s.bidNum === bid.num)) || {};
        const extraSpecItem = _.find(model.extraSpecs, s => s.position === spec.position && (s.bidNum === 0 || s.bidNum === bid.num));
        const bidSpecItem = _.find(bid.specifications, {position: spec.position}) || {};

        let item;

        if (extraSpecItem) {
          item = {
            ...extraSpecItem,
            currencyBid: _.result(extraSpecItem.bidCurrency, '[0].valueOf'),
            currencyPay: _.result(extraSpecItem.currencyContract, '[0].valueOf'),
            currencyClause: !!extraSpecItem.currencyClause.APPLICABLE
          };
        }
        else {
          item = {
            ...lotSpecItem,
            ...bidSpecItem,
            catalog: {
              id: bidSpecItem.additionNomenId || _.get(lotSpecItem, 'catalog.id'),
              name: bidSpecItem.additionNomenName || _.get(lotSpecItem, 'catalog.name')
            },
            okei: bidSpecItem.additionOkei || lotSpecItem.okei,
            okeiName: bidSpecItem.additionOkeiName || lotSpecItem.okeiName,
            maxPrice: _.get(lotSpecItem, 'price')
          };
        }

        const criteria = [].concat(
          model.criteria.lotPrice.filter(item => item.unit.CATALOG),
          model.criteria.price.filter(item => item.unit.CATALOG),
          model.criteria.expert.filter(item => item.unit.CATALOG),
          model.criteria.reduction.filter(item => item.unit.CATALOG)
        ).reduce((acc, c) => {
          const criterion = _.find(bidCriterions, {num: c.num, position: item.position}) || {};
          const v = _.get(criterion, 'value');

          acc[c.id] = {
            files: _.get(criterion, 'files', []),
            value: (v || v !== '' ? v : undefined),
            minValue: _.get(c, 'minValue'),
            maxValue: _.get(c, 'maxValue'),
            estimate: _.get(criterion, 'estimate')
          };
          return acc;
        }, {});

        const specItemPermissions = permissions.init(item.permissions);
        const stateOptions = [];

        if (specItemPermissions.CAN_APPROVE_SPEC_POSITION_IN_BID) {
          stateOptions.push({
            id: api.AdditionalSpecItemState.APPROVED,
            name: api.AdditionalSpecItemState.label('APPROVED')
          });
        }

        if (specItemPermissions.CAN_DECLINE_SPEC_POSITION_IN_BID) {
          stateOptions.push({
            id: api.AdditionalSpecItemState.DECLINED,
            name: api.AdditionalSpecItemState.label('DECLINED')
          });
        }

        if (stateOptions.length) {
          stateOptions.unshift({
            id: api.AdditionalSpecItemState.NONE,
            name: api.AdditionalSpecItemState.label('NONE'),
            disabled: true
          })
        }

        _.extend(spec, {
          dismiss: !!item.dismiss,
          parentPosition: item.parentPosition || 0,
          isTooHigh: !!item.tooHigh,
          isAdditional: item.isAdditional,
          isExtra: !!item.extraCost,
          extraCost: item.extraCost,
          extraCostDescription: item.extraCostDescription,
          checkDate: item.checkDate,
          catalog: item.catalog,
          okei: item.okei,
          okeiName: item.okeiName,
          multiplier: api.Number.fromAPI(item.multiplier),
          additionalNomenclature: item.additionNomen,
          count: item.count,
          price: item.price,
          maxPrice: item.maxPrice,
          presentPrice: item.presentPrice,
          currency: new api.Currency(item.currencyBid),
          currencyContract: new api.Currency(item.currencyPay),
          fixDate: new api.Enum.fromAPI(item.fixDate, api.FixDateChoice),
          currencyClause: item.currencyClause instanceof api.CurrencyClauseOptional ? !!item.currencyClause.APPLICABLE : item.currencyClause,
          syndicate: item.syndicate || undefined,
          winner: item.winner,
          percent: item.percent,
          permissions: specItemPermissions,
          state: item.state ? new api.AdditionalSpecItemState(item.state) : undefined,
          stateOptions,
          reason: item.reason,
          criteria
        });

        spec.isEmptyCurrency = !spec.currency.valid;

        if (spec.isExtra) {
          // check parent position
          const lotSpecItem = _.find(model.specs, s => s.position === spec.parentPosition && (s.bidNum === 0 || s.bidNum === bid.num));
          const bidSpecItem = _.find(bid.specifications, {position: spec.parentPosition});

          let parent = bidSpecItem;
          if (lotSpecItem) {
            parent = lotSpecItem;
          }

          // server has stored parts
          if (item.parts) {
            spec.parts = item.parts.map((e, i) => ({
              part: i + 1,
              serial: i + 1,
              count: e,
              disabled: e === 0,
            }));
          }
          else {
            spec.parts = (parent.parts || []).map((e, i) => ({
              part: i + 1,
              serial: i + 1,
              count: Math.trunc(e * 10000 / parent.count),
              disabled: e === 0,
            }));
          }

          const totalCount = _.reduce(spec.parts, (acc, p) => acc + p.count, 0);
          const lastPartIdx = _.findLastIndex(spec.parts, p => p.count > 0);
          if (lastPartIdx !== -1) {
            spec.parts[lastPartIdx].count += 10000 - totalCount;
          }
        }
        else if (!spec.isAdditional) {
          spec.parts = (lotSpecItem.parts || []).map((e, i) => ({part: i + 1, serial: i + 1, count: e}));
          spec.percent = lotSpecItem.percent;
          spec.count = lotSpecItem.count;
        }

        return spec;
      });

      // each additional item in specification that have no parent position will produce one more part
      const specForAdditionalParts = _.filter(fullSpecs, { isAdditional: true, parentPosition: 0});
      const nbOfAdditionalParts = specForAdditionalParts.length;

      // result.specs = _.concat([], originalSpecs, customSpecs).sort((a, b) => {
      //   const d = a.parentPosition - b.parentPosition;
      //   if (d === 0) return a.position - b.position;
      //   if (b.parentPosition && a.parentPosition) return d;
      //   if (b.parentPosition === 0) return a.parentPosition - b.position;
      //   return a.position - b.parentPosition;
      // });

      /// ORDER SPECs

      result.specs = fullSpecs.filter(e => e.parentPosition === 0).sort((a,b) => a.position - b.position);

      for (let i = 0; i < result.specs.length; ++i) {
        const children = fullSpecs
          .filter(e => e.parentPosition === result.specs[i].position)
          .sort((a,b) => a.isExtra !== b.isExtra ? b.isExtra - a.isExtra : a.position - b.position);
        result.specs.splice(i + 1, 0, ...children);
      }

      // result.specs = fullSpecs.sort((a, b) => {
      //   if (a.parentPosition === 0 && b.parentPosition === 0) return a.position - b.position;
      //   if (a.parentPosition !== 0 && b.parentPosition === 0) return a.parentPosition - b.position;
      //   if (a.parentPosition === 0 && b.parentPosition !== 0) return a.position - b.parentPosition;
      //
      //   if (a.parentPosition !== b.parentPosition) return a.parentPosition - b.parentPosition;
      //
      //   if (a.isExtra !== b.isExtra) return b.isExtra - a.isExtra;
      //   return a.position - b.position;
      //
      //   const d = a.parentPosition - b.parentPosition;
      //   if (d === 0) return a.position - b.position;
      //   if (b.parentPosition && a.parentPosition) return d;
      //   if (b.parentPosition === 0) return a.parentPosition - b.position;
      //   return a.position - b.parentPosition;
      // });

      let part = model.numberOfParts + 1;
      result.specs.reduce((acc, e) => {
        if (e.parentPosition === 0) {
          acc.major++;
          acc.minor = 0;
        }
        else acc.minor++;

        e.serial = acc.major;
        if (acc.minor) {
          e.serial += '.' + acc.minor;
        }

        if (e.isAdditional && e.parentPosition === 0) {
          // set proper part
          const serverPart = _.find(bid.requestParts, { position: e.position}) || {};
          e.parts = [{ part: serverPart.part, serial: part++, count: e.count}];
        }
        else if (e.isAdditional) {
          const parent = _.find(result.specs, { position: e.parentPosition });
          e.multiplier = e.multiplier === undefined ? 1 : e.multiplier;
          e.parts = parent.parts.map(p => ({ ...p, count: p.count * e.multiplier }));
          e.count = parent.count * e.multiplier;
          e.parentCount = parent.count;
          e.percent = parent.percent;
        }
        else if (e.isExtra) {
          const parent = _.find(result.specs, { position: e.parentPosition });
          e.parts.forEach((p, idx) => {
            p.part = parent.parts[idx].part;
            p.serial = parent.parts[idx].serial;
          })
        }

        return acc;
      },{major: 0, minor: 0});

      result.criteria = {
        expert: model.criteria.expert.map(e => {
          const criterion = _.find(bidCriterions, {num: e.num}) || {};

          return {
            value: undefined,
            ...e,
            ...criterion,
            showFileInput: e.valueType === 'File' || e.valueType === 'All',
            showTextInput: e.valueType === 'Text' || e.valueType === 'All',
            files: criterion.files || [],
            permissions: permissions.init(criterion.permissions || ''),

            till: api.Date.fromAPI(criterion.till),
            tillDescription: criterion.tillDescription
          }
        }),

        price: model.criteria.price.map((e, id) => {
          const criterion = _.find(bidCriterions, {num: e.num}) || {};

          return {
            value: undefined,
            ...e,
            ...criterion,
            permissions: permissions.init(criterion.permissions || ''),

            till: api.Date.fromAPI(criterion.till),
            tillDescription: criterion.tillDescription
          }
        }),

        reduction: model.criteria.reduction.map((e, id) => {
          const criterion = _.find(bidCriterions, {num: e.num}) || {};

          return {
            value: undefined,
            ...e,
            ...criterion,
            permissions: permissions.init(criterion.permissions || ''),

            till: api.Date.fromAPI(criterion.till),
            tillDescription: criterion.tillDescription
          }
        }),

        lotPrice: model.criteria.lotPrice.map(e => {
          const criterion = _.find(bidCriterions, {num: e.num}) || {};

          return {
            value: undefined,
            ...e,
            ...criterion,
            permissions: permissions.init(criterion.permissions || '')
          }
        }),

        reducedPrice: model.criteria.reducedPrice.map(e => {
          const criterion = _.find(bidCriterions, {num: e.num}) || {};

          return {
            ...e,
            ...criterion,
            permissions: permissions.init(criterion.permissions || '')
          }
        })
      };

      result.parts = Array.from({length: model.numberOfParts + nbOfAdditionalParts}, (v, i) => ({idx: i}));

      result.parts.forEach((part, idx) => {
        if (part.idx < model.numberOfParts) {
          part.part = part.idx + 1;
          part.serial = part.idx + 1;
        }
        else {
          const specItem = specForAdditionalParts[part.idx - model.numberOfParts];
          const bidItem = _.find(bid.requestParts, { position: specItem.position});
          part.part = bidItem.part;
          part.isAdditional = true;
          part.position = specItem.position;
          part.serial = bidItem.partView || (part.idx + 1);
        }

        const serverPart = _.find(bid.requestParts, { part: part.part}) || {};
        const lotPart = _.find(data.paymentParts, { partNum: part.part}) || {};

        const totalPrice = result.specs.reduce((acc, s) => {
          const specPart = _.find(s.parts, { part: part.part });
          return acc +  s.price * _.get(specPart, 'count', 0);
        }, 0);

        part.isEmptyCurrency = !!result.specs.reduce((acc, s) => {
          const specPart = _.find(s.parts, { part: part.part });
          const count = _.get(specPart, 'count', 0);

          if (count > 0) {
            return (acc === null ? true : acc) && s.isEmptyCurrency;
          }

          return acc;
        }, null);

        const isEmpty = result.specs.reduce((acc, s) => {
          const specPart = _.find(s.parts, { part: part.part });

          if (!specPart) {
            return acc;
          }

          if (s.isAdditional && s.parentPosition === 0) {
            return false;
          }

          return acc && (!!s.dismiss || !specPart.count);
        }, true);

        //const isEmpty = totalPrice === 0;
        const isDeliveryTimeRequired = part.isAdditional || !model.checkDates[part.part - 1] || !model.dates[part.part - 1];

        Object.assign(part, {
          ...part,
          bid: bid.id,
          cost: totalPrice,
          isEmpty: isEmpty,

          currency: new api.Currency(bid.currencyBid),
          signingDacopyTote: serverPart.dateSign ? new Date(serverPart.dateSign) : undefined,

          isDeliveryTimeRequired: isDeliveryTimeRequired && !isEmpty,
          deliveryTime: isDeliveryTimeRequired ? serverPart.deliveryTime : 0,

          deliveryDatetime: serverPart.deliveryDatetime ? new Date(serverPart.deliveryDatetime): undefined,
          receiveDatetime: serverPart.receiveDatetime ? new Date(serverPart.receiveDatetime) : undefined,

          isDeliveryDateRequired: part.isAdditional || model.checkDates[serverPart.part - 1],
          initialDeliveryDate: api.Date.fromAPI(model.dates[part.part - 1]),

          isReceiveDateRequired: part.isAdditional || model.checkDates[serverPart.part - 1],
          initialReceiveDate: api.Date.fromAPI(model.datesReceive[part.part - 1]),

          limits: {
            deliveryTime: {
              isFilled: lotPart.deliveryDays || lotPart.deliveryDaysExactly,
              value: api.Number.fromAPI(lotPart.deliveryDays, 0),
              precision: new api.Precision(!!lotPart.deliveryDaysExactly),
            },
            deliveryDatetime: {
              isFilled: lotPart.deliveryDate || lotPart.deliveryDateExactly,
              value: api.Date.fromAPI(lotPart.deliveryDate),
              precision: new api.Precision(!!lotPart.deliveryDateExactly),
            },
            receiveDatetime: {
              isFilled: lotPart.receiveDate || lotPart.receiveDateExactly,
              value: api.Date.fromAPI(lotPart.receiveDate),
              precision: new api.Precision(!!lotPart.receiveDateExactly),
            }
          },

          payments: Array.from({length: model.numberOfPayments}, (v, k) => {
            const payment = findPaymentTypeAndNum(k + 1, model.datePoints)
            return {
              part: part.part,
              bid: bid.id,
              num: payment.num,
              type: payment.type,
              payType: payment.payType
            }
          }).map(o => {
            const payment = _.find(bid.requestPartsDo, {part: o.part, num: o.num, payType: o.payType}) || {};
            // lotPayment contains limits filed by org
            const lotPayment = _.find(data.paymentPartDo, {part: o.part, num: o.num, payType: o.payType}) || {};

            applyRules(model, part, o);

            if (!o.isBlocked && isEmpty) {
              o.isBlocked = true;
              o.blockedReason = new api.AdvancePaymentBlockReason(api.AdvancePaymentBlockReason.EMPTY_PART);
            }

            return {
              ...o,
              duration: payment.days,
              percent: model.numberOfPayments === 1 ? 10000 : payment.prc,
              amount: totalPrice && payment.prc !== undefined ? totalPrice * payment.prc / 10000 : undefined,
              //commitDate: payment.dateFix ? new Date(payment.dateFix) : undefined,
              commitInfo: (payment.dateFix || []).map(e => ({
                date: api.Date.fromAPI(e.date),
                currencyBid: new api.Currency(e.currencyBid),
                currencyContract: new api.Currency(e.currencyPay),
                currencyClause: e.currencyClause,
                total: api.Number.fromAPI(e.summ),
                extraCost: e.extra
              })),
              limits: {
                days: {
                  isFilled: lotPayment.days || lotPayment.daysExactly,
                  value: api.Number.fromAPI(lotPayment.days, 0),
                  precision: new api.Precision(!!lotPayment.daysExactly),
                },
                percent: {
                  isFilled: lotPayment.prc || lotPayment.prcExactly,
                  value: api.Number.fromAPI(lotPayment.prc, 0),
                  precision: new api.Precision(!!lotPayment.prcExactly),
                }
              }
            }
          })
        });

        const lastPayment = _.maxBy(part.payments, p => {
          let value = p.num;

          if (p.type === 'delivery') {
            value += 100000;
          }
          else if (p.type === 'accept') {
            value += 200000;
          }

          return value;
        }) || {};

        part.payments.map(e => {
          //e.maxPaymentNum = _.max(_.filter(part.payments, { type: e.type }).map(e => e.num));
          e.lastPayment = _.pick(lastPayment, ['num', 'type']);
        });
      });

      result.parts = _.orderBy(result.parts, 'serial');

      return result;
    }), ['num']);

    model.request.documents = _.filter(model.documents, {type: 'ForRequest'}).map(e => {
      const doc = _.find(request.documents, {document: e.id}) || {};

      const availableDocuments = _.filter((request.requestFillDocuments || []), {document: e.id}).map(e => ({
        lot: e.lot,
        lotNum: e.lotNum,
        state: new api.RequestState(e.state),
        request: e.request,
        document: e.document
      }));

      return {
        ...e,
        ...doc,
        availableDocuments,
        expirationDate: doc.time ? new Date(doc.time) : undefined,
        state: new api.RequestDocumentState(doc.state),
        reason: doc.reason || undefined,
        regulationsReason: doc.reglamentReason || undefined,
        files: doc.files || [],
        permissions: permissions.init(doc.permissions || ''),
        till: api.Date.fromAPI(doc.till)
      }
    });

    model.request.latestBid = model.request.bids[model.request.bids.length - 1];

    model.request.clarifications = fillClarifications(data.request.clarifications || []);
  }

  validateExtraCosts(model);

  return model;
};

const validateExtraCosts = function(lot) {
  if (!lot.request) return;

  lot.request.bids.forEach(bid => {
    bid.specs.forEach(specItem => {
      if (specItem.isExtra && specItem.percent) {
        const parent = _.find(bid.specs, { position: specItem.parentPosition });
        specItem.price = parent.price * (parent.percent ? 1 : parent.count);
      }
    });
  });
};

const prepareData = function(lot) {
  const data = {
    criterions: [],
    paymentParts: [],
    paymentPartsDo: [],
    specifications: [],
    documents: lot.request.documents.map(d => ({
      document: d.id,
      time: d.expirationDate ? +d.expirationDate : undefined
    })),
    bids: lot.request.bids.map(bid => ({
      bid: bid.id,
      num: bid.num,
      description: bid.description || '',
      duration: bid.duration,
      currencyClause: !!bid.currencyClause,
      currencyBid: bid.currencyBid.valueOf(),
      currencyPay: bid.currencyPay.valueOf()
    })),
  };

  let isEditingForRetend = false;

  lot.request.bids.map(bid => {
    isEditingForRetend = isEditingForRetend || bid.isEditingForRetend;

    const criteria = [].concat(
      bid.criteria.expert,
      bid.criteria.price,
      bid.criteria.reduction,
      bid.criteria.lotPrice,
      bid.criteria.reducedPrice
    );

    // first copy all criteria which are not per spec position
    let bidCriteria = criteria.filter(c => !c.unit || !c.unit.CATALOG);

    // then for each spec position we should provide own criteria value
    const cs = criteria.filter(c => c.unit && c.unit.CATALOG).reduce((acc, c) => {
      bid.specs.map(s => {
        if (s.criteria && s.criteria[c.id]) {
          acc.push({
            ...c,
            value: s.criteria[c.id].value,
            position: s.position
          });
        }
      });

      return acc;
    }, []);

    bidCriteria = bidCriteria.concat(cs);

    data.criterions = data.criterions.concat(
      bidCriteria.map(e => ({
        num: e.num,
        value: _.isUndefined(e.value) ? undefined : e.value,
        bidNum: bid.num,
        bid: bid.id,
        position: e.position
      }))
    );

    data.paymentParts = data.paymentParts
      .concat(bid.parts.map(p => ({
        bid: p.bid,
        part: p.part,
        position: p.position,
        deliveryTime: p.isDeliveryTimeRequired ? api.Number.toAPI(p.deliveryTime) : undefined,
        deliveryDatetime: p.isDeliveryDateRequired ? api.Date.toAPI(p.deliveryDatetime) : undefined,
        receiveDatetime: p.isReceiveDateRequired ? api.Date.toAPI(p.receiveDatetime) : undefined
        // deliveryTime: p.isDeliveryTimeRequired ? p.deliveryTime : undefined,
        // deliveryDatetime: (p.isDeliveryDateRequired && !p.initialDeliveryDate ? +p.deliveryDatetime : undefined) || undefined,
        // receiveDatetime: (p.isDeliveryDateRequired && !p.initialDeliveryDate ? +p.receiveDatetime : undefined) || undefined
      })));

    data.paymentPartsDo = data.paymentPartsDo
      .concat(bid.parts.reduce((acc, p) => {
        p.payments.reduce((acc, e) => {
          if (e.isBlocked) return acc;

          acc.push({
            part: e.part,
            num: e.num,
            bid: e.bid,
            payType: e.payType,
            days: _.isUndefined(e.duration) ? undefined : e.duration,
            prc: _.isUndefined(e.percent) ? undefined : e.percent
          });

          return acc;
        }, acc);

        return acc;
      }, []));

    const fn = (formula, position, count) => {
      if (!formula) return;

      const variables = {};
      const criteria = bidCriteria.filter(e => (e.type === 'Price' || e.type === 'PriceLot') && (_.get(e.unit, 'LOT') || _.get(e.unit, 'CATALOG') && e.position === position));

      let v;

      try {
        for (let c of criteria) {
          let price = +c.value / 100;

          if (lot.method.REDUCTION && c.type === 'PriceLot') {
            price = price * (count || 1)
          }

          variables[c.alias.toLowerCase()] = price;
        }

        v = evaluate(formula.toLowerCase(), variables);

        if (!lot.method.REDUCTION) {
          v = Math.trunc(v / 100);
        }
      }
      catch(e) {
      }

      return _.isFinite(v) ? v : undefined;
    };

    data.specifications = data.specifications
      .concat(bid.specs.filter(e => !e.isExtra).map(e => ({
        position: e.position,
        parentPosition: e.parentPosition || undefined,
        additionNomen: e.additionalNomenclature || undefined,
        bid: bid.id,
        price: e.price,
        count: (e.isAdditional && e.parentPosition === 0) ? e.count : undefined,
        multiplier: e.multiplier,
        presentPrice: fn(lot.formula, e.position, e.count),
        currencyClause: !!e.currencyClause,
        currencyBid: api.Enum.toAPI(e.currency),
        currencyPay: api.Enum.toAPI(e.currencyContract),
        fixDate: api.Enum.toAPI(e.fixDate),
        syndicate: e.syndicate,
        percent: e.parentPosition === 0 ? e.percent : undefined,
      })));

    Array.prototype.push.apply(data.documents, bid.documents.map(d => ({
      bidNum: bid.num,
      document: d.id,
      time: d.expirationDate ? +d.expirationDate : undefined
    })));
  });

  data.isEditingForRetend = isEditingForRetend;

  return data;
};

export default {
  state: {
    publicLots: {},
    otherRequests: {}
  },

  getters: {
    publicLot: s => lotId => s.publicLots[lotId],
    otherRequest: s => (lotId, requestId, bidId = 0) => s.otherRequests[[lotId, requestId, bidId]]
  },

  mutations: {
    setPublicLot(s, data) {
      const model = createLot(data);
      Vue.set(s.publicLots, model.id, model);
    },

    updateRetendsInLot(s, data) {
      const retends = fillRetends(data);
      Vue.set(s.publicLots[data.id], 'retends', retends);
    },

    updateRequestsInLot(s, data) {
      const requests = fillRequests(data);
      Vue.set(s.publicLots[data.id], 'requests', requests);
      Vue.set(s.publicLots[data.id], 'requestsTotal', data.requestsTotal);
    },

    updateLotState(s, data) {
      Vue.set(s.publicLots[data.id], 'state', new api.LotState(data.state));
      Vue.set(s.publicLots[data.id], 'antisniper', api.Number.fromAPI(data.antisniper));
      Vue.set(s.publicLots[data.id], 'serverTime', +data.now);
      Vue.set(s.publicLots[data.id], 'serverDeltaTime', +data.now - Date.now());
      Vue.set(s.publicLots[data.id], 'isCommentAllowed', !data.commentTimeOut);
      Vue.set(s.publicLots[data.id], 'permissions', permissions.init(data.permissions));
      Vue.set(s.publicLots[data.id], 'nbOfLeaders', api.Number.fromAPI(data.countLeader));
    },

    updateBidMetrics(s, data) {
      const newLot = createLot(data);
      const currentLot = s.publicLots[data.id];

      if (!newLot || !newLot.request) return;
      if (!currentLot || !currentLot.request) return;

      console.log('updated ownbids and best');
      currentLot.request.ownBids = _.get(data, 'requestsTotal.ownBids', []);
      currentLot.request.leader = fillLeader(currentLot, data);

      (currentLot.request.bids || []).map(e => {
        const newBid = _.find(newLot.request.bids, { id: e.id });
        if (!newBid) return;

        Vue.set(e, 'presentPrice', newBid.presentPrice);
        Vue.set(e, 'presentPrice', newBid.presentPrice);
        Vue.set(e, 'estimate', newBid.estimate);
        Vue.set(e, 'rating', newBid.rating);
        Vue.set(e, 'minimalPresentPrice', newBid.minimalPresentPrice);
        Vue.set(e, 'otherEstimate', newBid.otherEstimate);
      });
    },

    setParsedBid(s, data) {
      const lot = s.publicLots[data.lotId];

      (data.specs || []).map(e => {
        const bid = _.find(lot.request.bids, {id: e.bid });
        const spec = _.find(bid.specs, { position: e.position });
        spec.price = e.price
      });

      (data.payments || []).map(e => {
        const bid = _.find(lot.request.bids, {id: e.bid });
        const part = _.find(bid.parts, { part: e.part });
        part.deliveryTime = e.deliveryTime;

        _.filter((data.paymentsDo || []), { bid: e.bid, part: e.part }).map(p => {
          const payment = _.find(part.payments, { num: p.num });
          payment.duration = p.days;
          payment.percent = p.prc;
          payment.amount = part.cost && payment.prc !== 0 ? part.cost * payment.prc / 10000 : undefined
        })
      });

      Vue.set(s.publicLots, lot.id, lot);
    },

    setOtherRequest(s, data) {
      const model = createLot(data);

      const bids = _.get(model.request, 'bids');
      const bid = _.find(bids, { historical: true });
      const bidId = _.get(bid, 'id', 0);

      Vue.set(s.otherRequests, [model.id, model.request.id, bidId], model);
    },

    changeEditModeForRetend(s, data) {
      const bids = _.get(s.publicLots[data.lotId], 'request.bids');
      const bid = _.find(bids, { id: data.bidId });

      if (bid) {
        bid.specs.map(e => {
          if (!e.isEmptyCurrency) {
            e.original = _.cloneDeep(e);
          }
        });

        bid.parts.map(p => {
          if (!p.isEmptyCurrency) {
            p.original = _.cloneDeep(p);
          }

          // org could change the delivery schedule, so we should modify participant's bid
          //   to make sure it's still valid

          if (!p.isDeliveryTimeRequired) {
            p.deliveryTime = 0;
          }

          if (!p.isDeliveryDateRequired) {
            p.deliveryDatetime = undefined
          }

          if (!p.isAcceptingDateRequired) {
            p.receDatetime = undefined
          }

          const lastPayment = _.maxBy(p.payments, p => {
            let value = p.num;

            if (p.type === 'delivery') {
              value += 100000;
            }
            else if (p.type === 'accept') {
              value += 200000;
            }

            return value;
          }) || {};

          p.payments.map(e => {
            //e.maxPaymentNum = _.max(_.filter(p.payments, { type: e.type }).map(e => e.num));
            //e.maxPaymentNum = _.max(p.payments.map(e => e.num));
            e.lastPayment = _.pick(lastPayment, ['num', 'type']);

            if (!p.isEmptyCurrency) {
              e.original = _.cloneDeep(e);
            }
          });
        });

        Vue.set(bid, 'isEditingForRetend', data.mode);
      }
    },

    addExtraCostItem(s, data) {
      const lot = s.otherRequests[[data.lotId, data.requestId, 0]];
      const bid = _.find(lot.request.bids, { id: data.bidId });

      if (!bid) return;

      const partItemIdx = bid.specs.findIndex(s => s.position === data.position);
      const specItem = bid.specs[partItemIdx];

      const parts = specItem.parts.map(p => {
        return {
          ...p,
          disabled: p.count === 0,
          count: Math.trunc(p.count * 10000 / specItem.count)
        }
      });

      const totalCount = _.reduce(parts, (acc, p) => acc + p.count, 0);
      const lastPartIdx = _.findLastIndex(parts, p => p.count > 0);
      if (lastPartIdx !== -1) {
        parts[lastPartIdx].count += 10000 - totalCount;
      }

      bid.specs.splice(partItemIdx + 1, 0, {
        editable: true,
        position: 0,
        parentPosition: data.position,
        isExtra: true,
        parts: parts,
        catalog: {
          id: undefined,
          name: undefined
        },
        currency: _.cloneDeep(specItem.currency),
        currencyContract: _.cloneDeep(specItem.currencyContract),
        fixDate: _.cloneDeep(specItem.fixDate),
        currencyClause: specItem.currencyClause,
        price: specItem.price * (specItem.percent ? 1 :specItem.count),
        request: specItem.request
      })
    }
  },

  actions: {
    handlePublicLot({commit}, data) {
      commit('setPublicLot', data);
    },

    handleOtherRequest({commit}, data) {
      commit('setOtherRequest', data);
    },

    async baseRequest({dispatch, getters}, {method, url, data, handler = 'handlePublicLot'}) {
      let error;

      try {
        const response = await api[method](url, data);
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch(handler, result);

          if (handler === 'handlePublicLot') {
            return getters.publicLot(result.id);
          }
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async fetchPublicLot({dispatch, getters}, {lotId}) {
      let error;

      try {
        const response = await api.get(`lots/${lotId}/public`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(lotId);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while creating new lot', e);
        error = e;
      }

      if (error) {
        throw error
      }
    },

    async checkPublicLot({commit}, {lotId}) {
      let error;

      try {
        const response = await api.get(`lots/${lotId}/public`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('updateRetendsInLot', result);
          commit('updateRequestsInLot', result);
          commit('updateLotState', result);
          commit('updateBidMetrics', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while creating new lot', e);
        error = e;
      }

      if (error) {
        throw error
      }
    },

    async updatePublicLot({dispatch, getters}, lot) {
      try {
        // send for all bids
        validateExtraCosts(lot);

        const data = prepareData(lot);

        if (data.isEditingForRetend) {
          return getters.publicLot(lot.id);
        }

        const response = await api.put(`/requests/${lot.request.id}`, data);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(lot.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while creating new lot', e);
        throw e;
      }
    },

    async getTextForBidInRetend({getters}, { lot, bid }) {
      try {
        // send for all bids
        const data = prepareData(lot);

        const response = await api.post(`/requests/${lot.request.id}/bid/${bid.num}/sign`, data);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          return result;
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while creating new lot', e);
        throw e;
      }
    },

    async signBidInRetend({dispatch, getters}, { lot, bid, text, signature }) {
      try {
        // send for all bids
        const data = prepareData(lot);

        const response = await api.post(`/requests/${lot.request.id}/addRetendBid/${bid.num}`, {
          dataForSign: text,
          sign: signature,
          ...data
        });
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(lot.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while creating new lot', e);
        throw e;
      }
    },

    async addFileToBid({dispatch, getters}, {lotId, requestId, bidId, sign, file}) {
      let error;

      try {
        const data = new FormData();
        data.append('sign', sign);
        data.append('file', file);

        const response = await api.post(`/requests/${requestId}/attach/${bidId}`, data);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async removeFileFromBid({dispatch, getters}, {lotId, requestId, bidId, fileId}) {
      try {
        const response = await api.delete(`/requests/${requestId}/attach/${bidId}/${fileId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async addFileToPublicLotDocument({dispatch}, {requestId, sign, file, docId}) {
      await dispatch('_addFileToPublicLotDocument', {requestId, bidId: 0, docId, sign, file});
    },

    async removeFileFromPublicLotDocument({dispatch}, {requestId, docId, fileId}) {
      await dispatch('_removeFileFromPublicLotDocument', {requestId, bidId: 0, docId, fileId});
    },

    async addFileToPublicLotBidDocument({dispatch}, {requestId, bidId, sign, file, docId}) {
      await dispatch('_addFileToPublicLotDocument', {requestId, bidId, docId, sign, file});
    },

    async removeFileFromPublicLotBidDocument({dispatch}, {requestId, bidId, docId, fileId}) {
      await dispatch('_removeFileFromPublicLotDocument', {requestId, bidId, docId, fileId});
    },

    async _addFileToPublicLotDocument({dispatch, getters}, {requestId, bidId, sign, file, docId}) {
      let error;

      try {
        const data = new FormData();
        data.append('sign', sign);
        data.append('file', file);

        const response = await api.post(`/requests/${requestId}/attach/document/${docId}/${bidId}`, data);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);

          return getters.publicLot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async _removeFileFromPublicLotDocument({dispatch, getters}, {requestId, bidId, docId, fileId}) {
      try {
        const response = await api.delete(`/requests/${requestId}/attach/document/${docId}/${fileId}/${bidId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);

          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async addFileToPublicLotCriterion({dispatch, getters}, {requestId, criterionId, bidId, position, file, sign}) {
      let error;

      try {
        const data = new FormData();
        data.append('sign', sign);
        data.append('file', file);

        let url = `/requests/${requestId}/attach/criterion/${criterionId}/${bidId}`;

        if (position !== undefined) {
          url = `${url}/${position}`;
        }

        const response = await api.post(url, data);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);

          return getters.publicLot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async removeFileFromPublicLotCriterion({dispatch, getters}, {requestId, criterionId, bidId, position, fileId}) {
      try {
        let url = `/requests/${requestId}/attach/criterion/${criterionId}/${fileId}/${bidId}`;
        if (position !== undefined) {
          url = `${url}/${position}`;
        }

        const response = await api.delete(url);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);

          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async addItemToSpecification({dispatch, getters}, {requestId, bidId, position}) {
      return await dispatch('baseRequest', {
        method: 'put',
        url: `/requests/${requestId}/${bidId}/${position}`
      });
    },

    async addExtraCostItemToSpecification({commit}, {lotId, requestId, bidId, position}) {
      commit('addExtraCostItem', {lotId, requestId, bidId, position});
    },

    async confirmAddingExtraCostItemToSpecification({dispatch, getters}, {lotId, requestId, bidId, bidNum, position, item}) {
      return await dispatch('baseRequest', {
        method: 'put',
        url: `/lots/${lotId}/extra`,
        handler: 'handleOtherRequest',
        data: {
          position: item.position,
          parentPosition: item.parentPosition || undefined,
          request: requestId,
          bid: bidId,
          bidNum: bidNum,
          price: item.price,
          description: item.extraCostDescription,
          count: item.count,
          currencyClause: !!item.currencyClause,
          currencyBid: api.Enum.toAPI(item.currency),
          currencyPay: api.Enum.toAPI(item.currencyContract),
          fixDate: api.Enum.toAPI(item.fixDate),
          percent: item.percent,
          extraCost: item.extraCost,
          parts: item.parts.map(p => p.count)
        }
      });
    },

    async removeExtraCostItemFromSpecification({dispatch, getters}, {lotId, requestId, bidNum, position, extraCost}) {
      return await dispatch('baseRequest', {
        method: 'delete',
        url: `/lots/${lotId}/extra/${requestId}/${bidNum}/${position}/${extraCost}`,
        handler: 'handleOtherRequest'
      });
    },

    async removeItemFromSpecification({dispatch, getters}, {requestId, bidId, position}) {
      return await dispatch('baseRequest', {
        method: 'delete',
        url: `/requests/${requestId}/${bidId}/${position}`
      });
    },

    async setNomenclatureInAdditionalSpecItem({dispatch, getters}, {requestId, bidId, position, okeId}) {
      return await dispatch('baseRequest', {
        method: 'put',
        url: `/requests/${requestId}/setNomen/${bidId}/${position}`,
        data: {
          nomen: okeId
        },
        handler: 'handleOtherRequest'
      });
    },

    async dismissItemInSpecification({dispatch, getters}, {requestId, bidId, position, dismiss}) {
      let error;

      try {
        const response = await api.put(`/requests/${requestId}/switchSpecification/${bidId}/${position}/${dismiss}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async addNewBidToRequest({dispatch, getters}, {requestId}) {
      try {
        const response = await api.post(`/requests/${requestId}/addBid`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);

          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async removeBidFromRequest({dispatch, getters}, {requestId, bidId}) {
      try {
        const response = await api.delete(`/requests/${requestId}/${bidId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);

          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async appendDividingCostFileToBid({dispatch, getters}, {requestId, bidId, file, sign}) {
      let error;

      try {
        const data = new FormData();
        data.append('sign', sign);
        data.append('file', file);

        const response = await api.put(`/requests/${requestId}/dividingPrice/${bidId}`, data);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async removeDividingCostFileFromBid({dispatch, getters}, {requestId, bidId, fileId}) {
      return await dispatch('baseRequest', {
        method: 'delete',
        url: `/requests/${requestId}/dividingPrice/${bidId}/${fileId}`
      });
    },

    async approveBid({dispatch, getters}, {requestId, bidId, verdict, reason, reglamentReason, acceptTime}) {
      return await dispatch('baseRequest', {
        method: 'post',
        url: `/requests/${requestId}/bid/${bidId}/${verdict}`,
        handler: 'handleOtherRequest',
        data: {
          reason,
          reglamentReason,
          acceptTime: api.Date.toAPI(acceptTime)
        }
      });
    },

    async approveAdditionalSpecItem({dispatch, getters}, {requestId, bidId, position, approved, reason}) {
      let error;

      try {
        reason = approved ? undefined : reason;
        const response = await api.post(`/requests/${requestId}/specification/${bidId}/${position}/${approved}`, {
          reason
        });
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handleOtherRequest', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async getRequestPublishSigningText({}, {requestId}) {
      let error;

      try {
        const response = await api.get(`/requests/${requestId}/sign`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return result;
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async signRequest({dispatch, getters}, {requestId, text, signature}) {
      let error;

      try {
        const response = await api.put(`/requests/${requestId}/commit`, {
          dataForSign: text,
          sign: signature
        });
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async getRequestRemoveSigningText({}, {requestId}) {
      try {
        const response = await api.get(`/requests/delete/${requestId}/sign`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return result;
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async revokeRequest({dispatch, getters}, {requestId, text, signature}) {
      try {
        const response = await api.delete(`/requests/${requestId}`, {
          data: {
            dataForSign: text,
            sign: signature
          }
        });
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async rejectRequest({dispatch, getters}, {requestId}) {
      try {
        const response = await api.delete(`/requests/${requestId}/reject`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async changeRequestOwner({dispatch, getters}, {requestId, owner}) {
      try {
        const response = await api.put(`/requests/${requestId}/setOwner/${owner}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async getDocumentLink({}, {lotId, fileId}) {
      let error;

      try {
        const response = await api.get(`/lots/download/${lotId}/${fileId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return result;
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error
      }
    },

    async fetchRequest({dispatch}, {lotId, requestId}) {
      let error;

      try {
        const response = await api.get(`/lots/${lotId}/public/${requestId}`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handleOtherRequest', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while creating new lot', e);
        error = e;
      }

      if (error) {
        throw error
      }
    },

    async fetchRequestWithHistory({dispatch}, {lotId, requestId, historyId, bidId}) {
      let error;

      try {
        const response = await api.get(`/lots/${lotId}/public/${requestId}`);
        let { status, result, errorCode } = response.data;
        const data = result;

        if (status === 200) {
          const bids = _.get(data, 'request.bids');
          const bid = _.find(bids, { id: historyId });

          if (!bid)  {
            const data = result;

            const response1 = await api.get(`/requests/${requestId}/history/bid/${historyId}`);
            // let { status, result, errorCode } = response1.data;

            data.request.bids.push({
              ...response1.data.result.bids[0],
              historical: true
            });
          }
          else {
            bid.historical = true;
          }

          dispatch('handleOtherRequest', data);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while creating new lot', e);
        error = e;
      }

      if (error) {
        throw error
      }
    },

    async addLotToFavorites({dispatch, getters}, { id }) {
      let error;

      try {
        const response = await api.post(`/lots/lot/${id}`);
        const {status, errorCode} = response.data;

        if (status !== 200) {
          error = new api.Errors(errorCode);
        }
        else {
          await dispatch('fetchUser', { userId: getters.currentUser.id });
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error
      }
    },

    async removeLotFromFavorites({dispatch, getters}, { id }) {
      let error;

      try {
        const response = await api.delete(`/lots/lot/${id}`);
        const {status, errorCode} = response.data;

        if (status !== 200) {
          error = new api.Errors(errorCode);
        }
        else {
          await dispatch('fetchUser', { userId: getters.currentUser.id });
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error
      }
    },

    async exportBidSpecification({}, { requestId, bidId, filename }) {
      let error;

      try {
        await api.download(`/requests/${requestId}/${bidId}/specification`);
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async exportReductionFinishList({}, { lotId, filename }) {
      let error;

      try {
        await api.download(`/lots/${lotId}/finishList`, {}, {
          filename: filename
        });
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async downloadBidSpecificationTemplate({}, { lotId, requestId, bidId, filename, method, selection, selectionMode }) {
      let error;

      try {
        await api.download(`/requests/${lotId}/${requestId}/${bidId}/specification`, {
          data: {
            type: method,
            specs: Object.keys(selection),
            mode: selectionMode
          }
        }, {
          method: 'post',
          filename: filename
        });
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async downloadAllDocumentsFromRequest({}, { lotId, requestId, bidNum = 0 }) {
      let error;

      try {
        await api.download(`/requests/${requestId}/document/${bidNum}`);
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async downloadAllFilesFromBid({}, { lotId, requestId, bidNum }) {
      let error;

      try {
        await api.download(`/requests/${requestId}/all/${bidNum}`);
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async uploadBidSpecification({getters, commit, dispatch}, { lotId, requestId, bidId, file }) {
      let error;

      const data = new FormData();
      data.append('file', file);

      try {
        const response = await api.post(`/requests/${requestId}/import/${bidId}/specification`, data);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          // commit('setParsedBid', {
          //   lotId,
          //   requestId,
          //   bidId,
          //   ...result
          // });
          const bidsInRetend = _.filter(getters.publicLot(result.id).request.bids, { isEditingForRetend: true });

          await dispatch('handlePublicLot', result);
          const lot = getters.publicLot(result.id);

          for (const bidInRetend of bidsInRetend) {
            const bid = _.find(lot.request.bids, { id: bidInRetend.id });

            if (bid) {
              await dispatch('enableEditBidForRetend', {
                lotId, requestId, bidId: bid.id, mode: true
              })
            }
          }

          return lot;
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async uploadReductionBidSpecificationTemplate({}, { requestId, file, priceCriteria }) {
      let error;

      try {
        const data = new FormData();
        data.append('file', file);

        const response = await api.post(`/requests/${requestId}/import/bid`, data);
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          const columns = Array.from({ length: result.columnCount }, (v, k) => {
            const p = [];
            do {
              p.push(String.fromCharCode(65 + k % 25));
              k = Math.trunc(k / 25);
            } while (k != 0);

            return p.reverse().join('');
          });

          const fields = [
            'position',
            'price',
            ...priceCriteria.map(e => ({
              isCriterion: true, label: e.criterionName,
              valueOf() {
                return `c${e.num}`
              }
            }))
          ];

          return {
            fileId: result.uid,
            fields: result.data.map((e, i) => ({
              name: fields[i] && fields[i].isCriterion ? fields[i] : new api.SpecificationTemplateField(fields[i]),
              column: e > 0 ? columns[e - 1]: undefined
            })),
            columns
          }
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async completeReductionBidSpecificationTemplateUpload({dispatch, getters}, { requestId, fileId, fields }) {
      let error;

      try {
        const data = { file: fileId };

        const response = await api.post(`/requests/${requestId}/import/bid/complete`, fields.reduce((acc, e) => {
          acc[e.name.valueOf()] = e.column;
          return acc;
        }, data));
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          return result;
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async exportBidPayments({}, { requestId, bidId, filename }) {
      let error;

      try {
        await api.download(`/requests/${requestId}/${bidId}/payment`);
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async uploadBidPayments({commit}, { lotId, requestId, bidId, file }) {
      let error;

      const data = new FormData();
      data.append('file', file);

      try {
        const response = await api.post(`/requests/${lotId}/import/${bidId}/payments`, data);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setParsedBid', {
            lotId,
            requestId,
            bidId,
            ...result
          })
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async addFileToProtocol({dispatch, getters}, { lotId, protocolType, file, sign}) {
      let error;

      const data = new FormData();
      data.append('sign', sign);
      data.append('file', file);

      try {
        const response = await api.post(`/lots/${lotId}/protocol/${protocolType}`, data);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async voteForDraft({dispatch, getters}, { lotId, requestId, bidId, protocolType, version, vote, reason}) {
      let error;

      try {
        const response = await api.post(`/lots/${lotId}/${protocolType}/${version}/${requestId}/${bidId}/vote/${api.Enum.toAPI(vote)}`, {
          reason
        });
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async addDraftToProtocol({dispatch, getters}, { lotId, protocolType, file, sign, timeTo, reason}) {
      let error;

      const data = new FormData();
      data.append('value', JSON.stringify({
        sign,
        reason
      }));
      data.append('file', file);

      try {
        const response = await api.post(`/lots/${lotId}/draftProtocol/${protocolType}/${api.Date.toAPI(timeTo)}`, data);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async restoreFileInProtocol({dispatch, getters}, { lotId, protocolType, version}) {
      let error;

      try {
        const response = await api.put(`/lots/${lotId}/protocol/${protocolType}/${version}`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async restoreDraftProtocol({dispatch, getters}, { lotId, protocolType, version}) {
      let error;

      try {
        const response = await api.put(`/lots/${lotId}/draftProtocol/${protocolType}/${version}`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async downloadReport({}, { lotId, protocolType, report}) {
      let error;

      try {
        const query = {
          absentia: report.voting === 'absentee',
          resolutionType: protocolType,
          com: report.members.map(e => `${e.id}-${e.presence === 'yes'}`)
        }

        if (report.decision === 'good') {
          query.resolutionType += 'Good';
        }
        else if (report.decision === 'bad') {
          query.resolutionType += 'Bad'
        }
        else {
          query.resolutionType += 'Other';
          query.resolution = report.reason;
        }

        await api.download(`/lots/${lotId}/protocol/${protocolType}`, { params: query });
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async approveDocumentInRequest({ dispatch, getters }, {requestId, bidId, docId, verdict, reason }) {
      let error;

      try {
        const response = await api.post(`/requests/${requestId}/${bidId || 0}/document/${docId}/${verdict}`, { reason });
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handleOtherRequest', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async approveRequest({ dispatch, getters }, {requestId, verdict, reason, reglamentReason, clarificationRequirement }) {
      let error;

      try {
        const response = await api.put(`/requests/${requestId}/${verdict}`, { reason, reglamentReason, clarificationRequirement });
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handleOtherRequest', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async saveCriterionEstimation({ dispatch, getters }, {requestId, bidId, criterionId, position, estimation }) {
      let error;

      try {
        let url = `/requests/${requestId}/${bidId}/${criterionId}`;

        if (position !== undefined) {
          url = `${url}/${position}`
        }

        url = `${url}/${estimation}`;

        const response = await api.put(url);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handleOtherRequest', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async allowDocumentUpdate({ dispatch, getters }, {requestId, bidId, docId, description, till }) {
      let error;

      try {
        const url = `/requests/${requestId}/${bidId || 0}/adjust/document/${docId}`;
        const response = await api.post(url, {
          description,
          till: api.Date.toAPI(till)
        });
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handleOtherRequest', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async allowCriterionUpdate({ dispatch, getters }, { requestId, criterionId, bid, description, till }) {
      let error;

      try {
        const response = await api.post(`/requests/${requestId}/adjust/criterion/${criterionId}/${bid}`, {
          description,
          till: api.Date.toAPI(till)
        });
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handleOtherRequest', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async allowBidUpdate({ dispatch, getters }, { requestId, bid, description, till }) {
      let error;

      try {
        const response = await api.post(`/requests/${requestId}/adjust/bid/${bid}`, {
          description,
          till: api.Date.toAPI(till)
        });

        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handleOtherRequest', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async requestClarification({ dispatch, getters }, { requestId, files, text, sign }) {
      await dispatch('requestBidClarification', { requestId, bidId: 0, files, text, sign })
    },

    async requestBidClarification({ dispatch, getters }, { requestId, bidId, files, text, sign }) {
      let error;

      try {
        const data = new FormData();
        data.append('value', JSON.stringify({
          text,
          sign,
          fileSigns: files.map(f => f.fileSign)
        }));

        for (const f of files) {
          data.append('file', f.file);
        }

        const response = await api.post(`/requests/${requestId}/clarification/${bidId}`, data);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handleOtherRequest', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async responseClarification({ dispatch, getters }, { requestId, clarificationId, text, sign, files}) {
      let error;

      try {
        const data = new FormData();
        data.append('value', JSON.stringify({
          text,
          sign,
          fileSigns: files.map(f => f.fileSign)
        }));

        for (const f of files) {
          data.append('file', f.file);
        }

        const response = await api.patch(`/requests/${requestId}/clarification/${clarificationId}`, data);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async downloadRequestClarificationAttachments({ dispatch, getters }, { requestId, questionId }) {
      let error;

      try {
        await api.download(`requests/${requestId}/${questionId}/true`);
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async downloadResponseClarificationAttachments({ dispatch, getters }, { requestId, questionId }) {
      let error;

      try {
        await api.download(`requests/${requestId}/${questionId}/false`);
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async createRetend({ dispatch, getters }, { lotId }) {
      let error;

      try {
        const response = await api.post(`/lots/${lotId}/retend`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async notifyAboutConditionalApprove({ dispatch, getters }, { requestId, filename }) {
      let error;

      try {
        await api.download(`/requests/${requestId}/downloadAccessNotice`, {}, {
          filename: filename + '.docx'
        });

        const { status, errorCode } = response.data;

        if (status !== 200) {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async enableEditBidForRetend({ commit, getters }, { lotId, requestId, bidId, mode }) {
      commit('changeEditModeForRetend', {
        lotId,
        requestId,
        bidId,
        mode
      });
    },

    async checkProtocolFileSignature({}, {lotId, type, version}) {
      try {
        const response = await api.get(`/lots/${lotId}/info/protocolSign/${type}/${version} `);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async checkTenderFileSignature({}, {tenderId, fileId}) {
      try {
        const response = await api.get(`/tenders/${tenderId}/info/sign/${fileId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async checkExpertCriteriaFileSignature({}, {requestId, fileId, bidId}) {
      try {
        const response = await api.get(`/requests/${requestId}/info/sign/${fileId}/${bidId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async checkFileOfLotDocumentSignature({}, {requestId, fileId}) {
      try {
        const response = await api.get(`/requests/${requestId}/info/sign/${fileId}/0`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async checkBidSignature({}, {requestId, bidId}) {
      try {
        const response = await api.get(`/requests/${requestId}/info/sign/0/${bidId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async checkRequestSignature({}, {requestId}) {
      try {
        const response = await api.get(`/requests/${requestId}/info/sign/0/0`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async checkBidFileSignature({}, {requestId, bidId, fileId}) {
      try {
        const response = await api.get(`/requests/${requestId}/info/sign/${fileId}/num/${bidId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async checkClarificationQuestionFileSignature({}, {requestId, questionId, fileNum}) {
      let error;

      try {
        const response = await api.get(`/requests/${requestId}/info/sign/${questionId}/clarification/true/1/${fileNum}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async checkClarificationResponseFileSignature({}, {requestId, questionId, fileNum}) {
      let error;

      try {
        const response = await api.get(`/requests/${requestId}/info/sign/${questionId}/clarification/false/1/${fileNum}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async attachBidResultFile({dispatch, getters}, {requestId, bidId, bidState, sign, file}) {
      const data = new FormData();
      data.append('sign', sign);
      data.append('file', file);

      return await dispatch('baseRequest', {
        method: 'post',
        url: `/requests/${requestId}/attach/state/${bidState}/${bidId}`,
        data,
        handler: 'handleOtherRequest'
      });
    },

    async attachBidAcceptFile({ dispatch }, {requestId, bidId, sign, file}) {
      return await dispatch('attachBidResultFile', {
        requestId,
        bidId,
        bidState: api.BidState.ACCEPTED,
        sign,
        file
      })
    },

    async attachBidDeclineFile({ dispatch }, {requestId, bidId, sign, file}) {
      return await dispatch('attachBidResultFile', {
        requestId,
        bidId,
        bidState: api.BidState.DECLINED,
        sign,
        file
      })
    },

    async exportBidResultFile({}, { requestId, bidId, filename, state }) {
      let error;

      try {
        await api.download(`/requests/${requestId}/bid/${bidId}/${state}`);
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async exportBidAcceptFile({ dispatch }, { requestId, bidId, filename }) {
      return await dispatch('exportBidResultFile', {
        requestId,
        bidId,
        filename,
        state: api.BidState.ACCEPTED
      })
    },

    async exportBidDeclineFile({ dispatch }, { requestId, bidId, filename }) {
      return await dispatch('exportBidResultFile', {
        requestId,
        bidId,
        filename,
        state: api.BidState.DECLINED
      })
    },

    async uploadTo1C({}, {lotId}) {
      let error;

      try {
        const response = await api.post(`/lots/${lotId}/send1CAccept`);
        const {status, errorCode} = response.data;

        if (status !== 200) {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async backToRetendFromAccept({dispatch}, {lotId, reason}) {
      return await dispatch('baseRequest', {
        method: 'put',
        url: `/lots/${lotId}/undo/Accept/true`,
        data: {
          reason
        }
      });
    },

    async backToAcceptFromResult({dispatch}, {lotId, reason}) {
      return await dispatch('baseRequest', {
        method: 'put',
        url: `/lots/${lotId}/undo/Result/true`,
        data: {
          reason
        }
      });
    },

    async changeBackToRetendFromAccept({dispatch}, {lotId, reason}) {
      let error;

      try {
        const response = await api.put(`/lots/${lotId}/undo/Accept/false`, { reason});
        const {status, errorCode, result} = response.data;


        if (status === 200) {
          dispatch('handlePublicLot', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async updateWinner({dispatch, getters}, { lotId, position, bid }) {
      let error;

      try {
        const response = await api.put(`lots/${lotId}/${position}/${bid || 0}`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          await dispatch('handlePublicLot', result);
          return getters.publicLot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async copyDocument({dispatch}, { lotId, requestId, documentId , sourceRequestId }) {
      let error;

      try {
        const response = await api.post(`requests/${requestId}/copy/${documentId}`, {
          requestFrom: sourceRequestId,
          requestsTo: [requestId]
        });
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          await dispatch('handlePublicLot', result);
        }
        else {
          const {content} = response.data;
          error = new api.Errors(errorCode, {
            details: JSON.parse(content || [])
          });
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    }
  }
}
